import { useContext, useState, useEffect, useRef } from "react";
import { Wrapper, Button, EmptyCart, ButtonLink, HeaderTemplate, CartTemplate, PageTemplate } from "../styles";
import { DataContext } from "../DataContext";
import { useLocation } from "react-router-dom";
import CartProduct from "../components/CartProduct";
import { Modal } from "react-bootstrap";
import SizeChart from "../components/SizeChart";
import toast from "react-hot-toast";
import { SIZE_KEY_MAP } from "../constants";

const Cart = () => {
  const { catalogProducts, cartProductsForUI, syncCartInventory, getSubTotal } = useContext(DataContext);
  const productsCount = cartProductsForUI.reduce((sum, product) => sum + product.quantity, 0);

  const [showModal, setShowModal] = useState(false);

  const location = useLocation();
  const lastProcessedLocationKeyRef = useRef(null);

  const CART_SYNC_DELAY = 1000;

  useEffect(() => {
    if (location.pathname !== "/bag") return;
    if (!cartProductsForUI?.length || !catalogProducts?.length) return;

    if (lastProcessedLocationKeyRef.current === location.key) return;
    lastProcessedLocationKeyRef.current = location.key;

    const itemsToRemove = [];
    const quantityAdjustments = [];

    cartProductsForUI.forEach((item) => {
      const { design, size, itemId, quantity, productName, inventory } = item;

      const inventoryKey = SIZE_KEY_MAP[size];
      if (!inventoryKey) return;

      const stock = Number(inventory?.[inventoryKey] || 0);

      if (stock <= 0) {
        itemsToRemove.push(itemId);
        return;
      }

      if (quantity > stock) {
        quantityAdjustments.push({
          itemId,
          quantity: stock,
          productName,
        });
      }

      if (stock === 0) {
        toast.custom(
          (t) => (
            <div className="custom-toast">
              <h2>{productName}</h2>
              <div>
                ({design} {size})
              </div>
              <p>is out of stock and was removed from your cart.</p>
              <div>
                <Button className="small autoWidth" onClick={() => toast.remove(t.id)}>
                  Close
                </Button>
              </div>
            </div>
          ),
          {
            duration: Infinity,
          },
        );

        itemsToRemove.push(itemId);
        return;
      }

      if (quantity > stock) {
        toast.custom(
          (t) => (
            <div className="custom-toast">
              <h2>{productName}</h2>
              <div>
                ({design} {size})
              </div>
              <p>
                quantity was adjusted from {quantity} to {stock} due to limited stock.
              </p>
              <div>
                <Button className="small autoWidth" onClick={() => toast.remove(t.id)}>
                  Close
                </Button>
              </div>
            </div>
          ),
          {
            duration: Infinity,
          },
        );

        quantityAdjustments.push({ itemId, quantity: stock });
      }
    });

    if (!itemsToRemove.length && !quantityAdjustments.length) return;

    setTimeout(() => {
      syncCartInventory(itemsToRemove, quantityAdjustments);
    }, CART_SYNC_DELAY);
  }, [location.key, location.pathname, cartProductsForUI, catalogProducts, syncCartInventory]);

  return (
    <>
      {productsCount > 0 ? (
        <>
          <HeaderTemplate className="narrow">
            <div className="header">
              <div className="title">
                <h1>Shopping Bag</h1>
              </div>
            </div>
          </HeaderTemplate>

          <CartTemplate className="cart-template">
            <div>
              <div className="cart-products-column">
                {cartProductsForUI.map((currentProduct, idx) => (
                  <CartProduct key={idx} product={currentProduct} />
                ))}
              </div>
            </div>

            <div className="cart-bottom-boxes">
              <div className="size-guide">
                <span>
                  Before checking out, review the <strong>Size Guide</strong>
                </span>
                <Button className="size-guide-button white" onClick={() => setShowModal(true)}>
                  View Size Guide
                </Button>
              </div>

              <div className="cart-checkout-box">
                <div className="cart-total">
                  Subtotal<span>${getSubTotal()}</span>
                </div>
                <ButtonLink className="checkout-button yellow" to="/checkout">
                  Checkout
                </ButtonLink>
              </div>
            </div>
          </CartTemplate>

          <Modal size="xl" fullscreen={"md-down"} show={showModal} animation={false} onHide={() => setShowModal(false)}>
            <Wrapper className="modal-styles">
              <Modal.Header closeButton>
                <Modal.Title>Size Guide</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <PageTemplate className="size-guide-modal">
                  <SizeChart />
                </PageTemplate>
              </Modal.Body>
            </Wrapper>
          </Modal>
        </>
      ) : (
        <EmptyCart>
          <h1>Your Shopping Bag is Empty</h1>
        </EmptyCart>
      )}
    </>
  );
};

export default Cart;
